import { MAX_TILES_LIMIT } from '@/features/Dashboard/constants/const';
import {
  MarketApp,
  MarketApps,
  MarketAppWithTabs,
  TileBigNumber,
  TilesWidgetV2,
  WidgetV2
} from '@/features/Dashboard/types';
import { extractIds } from '@/utils/extractIds';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';
import { isSameSymbol } from '@/utils/isSameSymbol';

/**
 * Build preview config from tiles collection and market apps with tabs.
 */
export const buildPreviewConfig = (
  tiles: TileBigNumber[],
  marketTabApps: MarketAppWithTabs[]
): MarketApps => {
  const tilesPart: MarketApps =
    tiles.length > 0
      ? [
          {
            type: 'tiles' as const,
            tiles,
            name: 'Tiles',
            metadata: {},
            layout: null,
            id: 'tiles'
          } satisfies TilesWidgetV2
        ]
      : [];

  return [...tilesPart, ...marketTabApps];
};

export type AppendTilesResult = {
  tiles: TileBigNumber[];
  exceeded: boolean;
  limitedCount: number;
  duplicatesCount: number;
  addedTiles: TileBigNumber[];
  duplicateCodes: string[];
};

/**
 * Returns new tiles array with deterministic IDs appended for each tile
 * from provided tiles widget (no mutations).
 * Filters out duplicates based on symbol Code and limits to MAX_TILES_LIMIT.
 */
export const appendTilesFromWidget = (
  prev: TileBigNumber[],
  widget: TilesWidgetV2
): AppendTilesResult => {
  // Remove duplicates using symbol equality
  const existingSymbols = prev.map(tile => tile.symbol.key);
  const newTiles = widget.tiles.filter(
    tile => !existingSymbols.some(es => isSameSymbol(es, tile.symbol.key))
  );

  // Get duplicate codes
  const duplicateTiles = widget.tiles.filter(tile =>
    existingSymbols.some(es => isSameSymbol(es, tile.symbol.key))
  );
  const duplicateCodes = duplicateTiles.map(tile => tile.symbol.key.Code);

  // Count duplicates
  const duplicatesCount = widget.tiles.length - newTiles.length;

  // Check if we'll exceed the limit
  const totalTiles = prev.length + newTiles.length;
  const exceeded = totalTiles > MAX_TILES_LIMIT;

  // Limit to MAX_TILES_LIMIT
  const availableSlots = MAX_TILES_LIMIT - prev.length;
  const tilesToAdd = newTiles.slice(0, Math.max(0, availableSlots));

  const existingTilesIds = extractIds(prev);

  const tilesWithIds = tilesToAdd.map(tile => {
    const newTileId = generateUniqueUUID(existingTilesIds);
    existingTilesIds.add(newTileId);
    return { ...tile, id: newTileId };
  });

  return {
    tiles: [...prev, ...tilesWithIds],
    exceeded,
    limitedCount: tilesWithIds.length,
    duplicatesCount,
    addedTiles: tilesWithIds,
    duplicateCodes
  };
};

/**
 * Adds either a new app with tabs or appends a single tab to an existing app identified by insertAtId.
 * Returns new apps array. No mutations.
 */
export const addWidgetWithTabs = (
  prev: MarketAppWithTabs[],
  widget: Extract<MarketApp, MarketAppWithTabs>,
  insertAtId: string | null
): MarketAppWithTabs[] => {
  const existingTabs = prev.flatMap(app => app.tabs.map(tab => tab));
  const existingTabIds = extractIds(existingTabs);
  const newTabId = generateUniqueUUID(existingTabIds);

  if (insertAtId) {
    const tabToAdd = widget.tabs[0];

    return prev.map(app => {
      if (app.id !== insertAtId) return app;
      return {
        ...app,
        tabs: [...app.tabs, { ...tabToAdd, id: newTabId }]
      };
    });
  }

  const existingAppIds = extractIds(prev);
  const newAppId = generateUniqueUUID(existingAppIds);

  return [
    ...prev,
    {
      ...widget,
      id: newAppId,
      tabs: widget.tabs.map((tab, i) => ({ ...tab, id: newTabId }))
    }
  ];
};

/** Update specific tab in a specific app. Keeps original tab id. */
export const updateTabInApps = (
  prev: MarketAppWithTabs[],
  appId: string,
  tabId: string,
  updatedTab: WidgetV2
): MarketAppWithTabs[] => {
  return prev.map(app => {
    if (app.id !== appId) return app;
    return {
      ...app,
      tabs: app.tabs.map(t => (t.id === tabId ? { ...updatedTab, id: t.id } : t))
    };
  });
};

/** Update a single tile by id. Keeps original tile id. */
export const updateTileInTiles = (
  prev: TileBigNumber[],
  tileId: string,
  updatedTile: TileBigNumber
): TileBigNumber[] => {
  return prev.map(t => (t.id === tileId ? { ...updatedTile, id: t.id } : t));
};
